import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MoneyInput } from '@/components/money/MoneyInput';
import { RateInput } from '@/components/money/RateInput';
import { ProjectionSummary } from '@/components/debts/ProjectionSummary';
import { DEBT_TYPE_LABELS } from '@shared/labels';
import type { DebtType, InterestRateType } from '@shared/types';
import { simulateFreeProjection } from '@/hooks/useProjection';

export function SimulatorPage() {
  const [type, setType] = useState<DebtType>('REVOLVING');
  const [balance, setBalance] = useState(10_000_000);
  const [ratePercent, setRatePercent] = useState(28);
  const [rateType, setRateType] = useState<InterestRateType>('EA');
  const [termMonths, setTermMonths] = useState(60);
  const [monthlyPayment, setMonthlyPayment] = useState(500_000);

  const isFixed = type === 'FIXED_INSTALLMENT';

  const { data: result, isFetching } = useQuery({
    queryKey: ['simulator', type, balance, ratePercent, rateType, termMonths, monthlyPayment, isFixed],
    queryFn: () =>
      simulateFreeProjection({
        type,
        balance,
        interestRate: ratePercent / 100,
        interestRateType: rateType,
        termMonths: isFixed ? termMonths : null,
        monthlyPayment: monthlyPayment > 0 ? monthlyPayment : undefined,
      }),
    enabled: balance > 0 && ratePercent >= 0 && (isFixed || monthlyPayment > 0),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Simulador</h1>
        <p className="text-sm text-muted-foreground">
          "¿Y si tuviera una deuda así?" — no guarda nada, es solo para explorar escenarios.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card space-y-4 lg:col-span-1">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tipo de deuda</label>
            <Select value={type} onChange={(e) => setType(e.target.value as DebtType)}>
              {Object.entries(DEBT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Saldo</label>
            <MoneyInput value={balance} onChange={setBalance} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tasa de interés</label>
            <RateInput ratePercent={ratePercent} rateType={rateType} onChangeRate={setRatePercent} onChangeType={setRateType} />
          </div>
          {isFixed && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Plazo (meses)</label>
              <Input type="number" min={1} value={termMonths} onChange={(e) => setTermMonths(Number(e.target.value))} />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {isFixed ? 'Abono extra (opcional, encima de la cuota)' : 'Abono mensual'}
            </label>
            <MoneyInput value={monthlyPayment} onChange={setMonthlyPayment} />
          </div>
        </div>

        <div className="lg:col-span-2">
          {isFetching ? <p className="text-sm text-muted-foreground">Calculando…</p> : result ? <ProjectionSummary result={result} /> : null}
        </div>
      </div>
    </div>
  );
}
